import { useState } from 'react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import {
  Loader2, DollarSign, AlertTriangle, Zap,
} from 'lucide-react';
import { CondicionMedioPagoSelect } from '../../components/CondicionMedioPagoSelect';
import { SolicitanteAutocomplete } from '../../components/compras/SolicitanteAutocomplete';

const fmt = (num) => Math.round(num || 0).toLocaleString('es-CL');

function DTETypeAlert({ tipoDte }) {
  if (tipoDte === '61') {
    return (
      <div className="bg-red-50 border border-red-300 rounded-lg p-3 flex items-start gap-2" data-testid="alert-nc-contab">
        <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-bold text-red-700">Nota de Credito</p>
          <p className="text-xs text-red-600">Este monto se descontara del proveedor. Se registrara como valor negativo en contabilizacion.</p>
        </div>
      </div>
    );
  }
  if (tipoDte === '56') {
    return (
      <div className="bg-orange-50 border border-orange-300 rounded-lg p-3 flex items-start gap-2" data-testid="alert-nd-contab">
        <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-bold text-orange-700">Nota de Debito</p>
          <p className="text-xs text-orange-600">Este monto se sumara a la deuda con el proveedor.</p>
        </div>
      </div>
    );
  }
  if (tipoDte === '52') {
    return (
      <div className="bg-gray-50 border border-gray-300 rounded-lg p-3 flex items-start gap-2" data-testid="alert-gd-contab">
        <AlertTriangle className="w-5 h-5 text-gray-500 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-bold text-gray-700">Guia de Despacho</p>
          <p className="text-xs text-gray-600">Solo referencial. Los montos pueden no ser exactos. Espere la factura para contabilizar.</p>
        </div>
      </div>
    );
  }
  return null;
}

function ItemsPreviewTable({
  items, cuentasDisponibles, otList, itemsCtas, setItemsCtas,
  itemsOts, setItemsOts, expressOT, previewData,
}) {
  return (
    <div>
      <p className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-1">
        <DollarSign className="w-3.5 h-3.5 text-teal-600" />
        Preview Contabilizacion en Alegra ({items.length} items)
      </p>
      <div className="border rounded overflow-hidden">
        <table className="w-full text-xs" data-testid="preview-items-table">
          <thead>
            <tr className="bg-gradient-to-r from-teal-600 to-teal-700 text-white text-[10px]">
              <th className="px-2 py-1.5 text-left w-8">#</th>
              <th className="px-2 py-1.5 text-left">Item DTE</th>
              <th className="px-2 py-1.5 text-right w-14">Cant.</th>
              <th className="px-2 py-1.5 text-right w-20">Neto</th>
              <th className="px-2 py-1.5 text-left min-w-[130px]">OT</th>
              <th className="px-2 py-1.5 text-left min-w-[180px]">Cuenta Contable</th>
              <th className="px-2 py-1.5 text-center w-12">Match</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const selectedCta = itemsCtas[item.linea] || '';
              const selectedOt = itemsOts[item.linea] || expressOT || '';
              return (
                <tr key={item.linea} className="border-t hover:bg-gray-50">
                  <td className="px-2 py-1.5 text-gray-500">{item.linea}</td>
                  <td className="px-2 py-1.5">
                    <span className="font-medium block truncate max-w-[200px]" title={item.nombre}>{item.nombre}</span>
                    {item.descripcion_extra && item.descripcion_extra !== item.nombre && (
                      <span className="text-[9px] text-gray-400 block truncate">{item.descripcion_extra}</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">{item.cantidad}</td>
                  <td className="px-2 py-1.5 text-right font-mono font-medium">${fmt(item.monto_neto)}</td>
                  <td className="px-2 py-1.5">
                    <select
                      value={selectedOt}
                      onChange={(e) => {
                        const newOt = e.target.value;
                        setItemsOts(prev => ({ ...prev, [item.linea]: newOt }));
                        const otCtaMap = previewData?.ot_cta_map || {};
                        if (otCtaMap[newOt]) {
                          setItemsCtas(prev => ({ ...prev, [item.linea]: otCtaMap[newOt] }));
                        }
                      }}
                      className={`w-full text-[10px] border rounded px-1 py-1 ${
                        selectedOt ? 'border-blue-300 bg-blue-50' : 'border-gray-300'
                      }`}
                      data-testid={`ot-item-${item.linea}`}>
                      <option value="">-- OT --</option>
                      {otList.map(ot => (
                        <option key={ot.numero_ot} value={ot.numero_ot}>
                          {ot.numero_ot} - {(ot.descripcion || '').substring(0, 25)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1.5">
                    <select
                      value={selectedCta}
                      onChange={(e) => setItemsCtas(prev => ({ ...prev, [item.linea]: e.target.value }))}
                      className={`w-full text-[10px] border rounded px-1.5 py-1 ${
                        selectedCta ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'
                      }`}
                      data-testid={`cta-select-${item.linea}`}
                    >
                      <option value="">-- Seleccionar cuenta --</option>
                      {(cuentasDisponibles || []).map(c => (
                        <option key={c.code} value={c.code}>
                          {c.code} - {c.nombre}
                        </option>
                      ))}
                    </select>
                    {item.match_material && (
                      <span className="text-[9px] text-green-600 block mt-0.5 truncate" title={item.match_material}>
                        Match: {item.match_material}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    {item.confidence === 'alta' && <span className="inline-block w-2 h-2 rounded-full bg-green-500" title="Confianza alta" />}
                    {item.confidence === 'media' && <span className="inline-block w-2 h-2 rounded-full bg-yellow-500" title="Confianza media" />}
                    {item.confidence === 'baja' && <span className="inline-block w-2 h-2 rounded-full bg-orange-500" title="Confianza baja" />}
                    {item.confidence === 'ninguna' && <span className="inline-block w-2 h-2 rounded-full bg-red-400" title="Sin match" />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex gap-3 mt-1.5 text-[9px] text-gray-500">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Alta</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500 inline-block" /> Media</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500 inline-block" /> Baja</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400 inline-block" /> Sin match</span>
      </div>
    </div>
  );
}

function AlegraPreviewSummary({ items, itemsCtas, itemsOts, expressOT, cuentasDisponibles, totales }) {
  return (
    <div className="bg-gray-50 border rounded p-3">
      <p className="text-[10px] font-bold text-gray-600 mb-1.5">Preview Contabilizacion en Alegra:</p>
      <div className="space-y-1">
        {(items || []).map(item => {
          const cta = itemsCtas[item.linea] || '';
          const ctaInfo = (cuentasDisponibles || []).find(c => c.code === cta);
          const itemOt = itemsOts[item.linea] || expressOT || '';
          return (
            <div key={item.linea} className="flex justify-between text-[10px]">
              <span className={cta ? 'text-gray-700' : 'text-red-500'}>
                {cta ? `${cta} - ${ctaInfo?.nombre || ''}` : 'SIN ASIGNAR'}
                {itemOt ? <span className="text-blue-600 ml-1">(OT {itemOt})</span> : ''}
              </span>
              <span className="font-mono font-medium">${fmt(item.monto_neto)} + IVA</span>
            </div>
          );
        })}
        <div className="border-t pt-1 mt-1 flex justify-between text-xs font-bold">
          <span>Total Neto</span>
          <span className="text-teal-700">${fmt(totales?.neto)}</span>
        </div>
      </div>
    </div>
  );
}

export function OCExpressModal({
  open, onClose, previewData, previewLoading,
  expressOT, setExpressOT, itemsCtas, setItemsCtas,
  // Iter 389 (Renzo): Solicitante obligatorio en OC Express
  expressSolicitante = '', setExpressSolicitante = () => {},
  itemsOts, setItemsOts, otList, otFiltro, setOtFiltro, loadOts,
  // Iter 365 — par canónico Condición (CP-*) + Medio de Pago (MP-*); sin texto libre.
  expressCondMP, setExpressCondMP,
  expressProcesando, onExpressOC, onSoloVbAgregar,
  // Iter 235.i (Carlos): banner cuando reemplazamos una OC incorrecta
  reemplazarOcActual = false, ocAnterior = null,
}) {
  const dteTypeLabel = previewData?.tipo_dte === '61' ? 'Nota de Credito'
    : previewData?.tipo_dte === '56' ? 'Nota de Debito'
    : previewData?.tipo_dte === '52' ? 'Guia de Despacho'
    : 'Factura';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" /> OC Express + Contabilizacion
            {previewData?.proximo_oc_express && (
              <span className="ml-auto text-xs font-mono bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 rounded-md">
                Próxima OC: <strong>{previewData.proximo_oc_express}</strong>
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4" data-testid="express-modal">
          {/* Iter 235.i (Carlos): banner cuando estamos reemplazando una OC incorrecta */}
          {reemplazarOcActual && ocAnterior && (
            <div className="bg-orange-50 border border-orange-300 rounded-lg p-3 flex items-start gap-2" data-testid="alert-reemplazo-oc-contab">
              <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-bold text-orange-800">Reemplazo de OC incorrecta</p>
                <p className="text-xs text-orange-700">
                  Vas a desasociar la <b>OC {ocAnterior}</b> (declarada por el proveedor pero no corresponde)
                  y a crear una <b>OC Express</b> en su lugar. Quedará auditoría en el DTE.
                </p>
              </div>
            </div>
          )}
          {previewLoading && (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
          )}
          {previewData && (
            <>
              <DTETypeAlert tipoDte={previewData.tipo_dte} />

              {/* Header info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="text-[10px] font-bold text-blue-700">Proveedor</p>
                  <p className="text-sm font-medium">{previewData.emisor?.razon_social}</p>
                  <p className="text-[10px] text-gray-500">RUT: {previewData.emisor?.rut}</p>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-white border rounded p-2 text-center">
                    <p className="text-[10px] text-gray-500">Neto</p>
                    <p className="text-sm font-bold">${fmt(previewData.totales?.neto)}</p>
                  </div>
                  <div className="bg-white border rounded p-2 text-center">
                    <p className="text-[10px] text-gray-500">IVA</p>
                    <p className="text-sm font-bold">${fmt(previewData.totales?.iva)}</p>
                  </div>
                  <div className="bg-teal-50 border border-teal-200 rounded p-2 text-center">
                    <p className="text-[10px] text-teal-700">Total</p>
                    <p className="text-sm font-bold text-teal-700">${fmt(previewData.totales?.total)}</p>
                  </div>
                </div>
              </div>

              {/* OT Dropdown + Filter */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">OT por defecto (aplica a todos los items)</label>
                  <div className="flex gap-1 mb-1">
                    {[
                      { key: 'ejecucion', label: 'En Ejecucion' },
                      { key: 'admin', label: 'Admin (99x)' },
                      { key: 'todas', label: 'Todas' },
                    ].map(opt => (
                      <button key={opt.key}
                        onClick={() => { setOtFiltro(opt.key); loadOts(opt.key); }}
                        className={`px-2 py-0.5 rounded text-[9px] font-medium transition-all ${
                          otFiltro === opt.key
                            ? 'bg-teal-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        data-testid={`ot-filter-${opt.key}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <select
                    value={expressOT}
                    onChange={(e) => {
                      const ot = e.target.value;
                      setExpressOT(ot);
                      const newOts = {};
                      (previewData?.items || []).forEach(it => { newOts[it.linea] = ot; });
                      setItemsOts(newOts);
                      const otCtaMap = previewData?.ot_cta_map || {};
                      if (otCtaMap[ot]) {
                        const newCtas = {};
                        (previewData?.items || []).forEach(it => { newCtas[it.linea] = otCtaMap[ot]; });
                        setItemsCtas(prev => ({ ...prev, ...newCtas }));
                      }
                    }}
                    className="w-full border rounded px-2 py-1.5 text-xs"
                    data-testid="express-ot-input">
                    <option value="">-- Seleccionar OT --</option>
                    {otList.map(ot => (
                      <option key={ot.numero_ot} value={ot.numero_ot}>
                        {ot.numero_ot} - {(ot.descripcion || '').substring(0, 40)} ({ot.estado})
                      </option>
                    ))}
                  </select>
                  <p className="text-[10px] text-gray-400 mt-0.5">{otList.length} OTs disponibles — Puede cambiar OT por item en la tabla</p>
                </div>
                <div className="space-y-2">
                  <SolicitanteAutocomplete value={expressSolicitante} onChange={setExpressSolicitante} testid="express-solicitante-input" />
                  <div className="flex items-center justify-center bg-amber-50 border border-amber-200 rounded p-2">
                    <span className="text-xs text-amber-800">
                      {dteTypeLabel} <strong className="text-amber-900">{previewData.folio}</strong>
                      {previewData.orden_compra ? ` (OC ref: ${previewData.orden_compra})` : ' (Sin OC)'}
                    </span>
                  </div>
                </div>
              </div>

              <ItemsPreviewTable
                items={previewData.items || []}
                cuentasDisponibles={previewData.cuentas_disponibles || []}
                otList={otList}
                itemsCtas={itemsCtas}
                setItemsCtas={setItemsCtas}
                itemsOts={itemsOts}
                setItemsOts={setItemsOts}
                expressOT={expressOT}
                previewData={previewData}
              />

              <CondicionMedioPagoSelect
                value={expressCondMP}
                onChange={setExpressCondMP}
                testIdPrefix="express-fp"
              />

              <AlegraPreviewSummary
                items={previewData.items || []}
                itemsCtas={itemsCtas}
                itemsOts={itemsOts}
                expressOT={expressOT}
                cuentasDisponibles={previewData.cuentas_disponibles || []}
                totales={previewData.totales}
              />
            </>
          )}
          {!previewLoading && !previewData && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
              No se pudo cargar el preview de la factura.
            </div>
          )}
        </div>
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onSoloVbAgregar} data-testid="solo-vb-btn">Solo Dar VB</Button>
          <Button onClick={onExpressOC}
            disabled={
              expressProcesando ||
              !previewData ||
              !expressCondMP?.condicion_codigo ||
              (expressCondMP?.requiere_medio_pago && !expressCondMP?.medio_pago_codigo)
            }
            title={
              !expressCondMP?.condicion_codigo
                ? 'Seleccione una condición de pago vigente'
                : (expressCondMP?.requiere_medio_pago && !expressCondMP?.medio_pago_codigo)
                  ? 'Seleccione el medio de pago'
                  : ''
            }
            className="bg-amber-500 hover:bg-amber-600" data-testid="express-oc-btn">
            {expressProcesando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            Crear OC Express
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
